import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { isCanonicalGastronomicCatalogNavigation } from "../../utils/siteExperienceDefaults";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import NavbarBrand from "./NavbarBrand";
import { safeTextSx } from "../../utils/textSafety";
import {
  navbarActionStyle,
  navbarBusinessNameColor,
  navbarCustomButtonStyle,
  navbarDrawerStyle,
  navbarLogoSurfaceStyle,
  navbarLinkStyle,
  navbarMobileOwner,
} from "./navbarVisualOwnership";

function DrawerNavItem({ active, label, fieldPath, onClick, endIcon, style }) {
  if (!String(label ?? "").trim()) return null;
  const owner = style || {};
  const textColor = active ? owner.activeTextColor : owner.textColor;
  const backgroundColor = active ? owner.activeBackgroundColor : owner.backgroundColor;
  const borderColor = active ? owner.activeBorderColor : owner.borderColor;
  return (
    <ListItemButton
      data-landing-field={fieldPath || undefined}
      onClick={onClick}
      sx={{
        minHeight: owner.minHeight ?? 52,
        px: 1.5,
        borderRadius: `${owner.borderRadius ?? 18}px`,
        color: textColor,
        bgcolor: backgroundColor,
        border: "1px solid",
        borderColor,
        "&:hover": {
          bgcolor: owner.hoverBackgroundColor,
          color: owner.hoverTextColor,
          borderColor: owner.hoverBorderColor,
        },
        "&:focus-visible": {
          outline: `3px solid ${owner.focusColor || "#F35A3A"}`,
          outlineOffset: 2,
        },
      }}
    >
      <Typography
        title={label}
        sx={{
          ...safeTextSx,
          flex: 1,
          fontSize: `${owner.fontSize ?? 16}px`,
          fontWeight: active ? (owner.activeFontWeight ?? 900) : (owner.fontWeight ?? 800),
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexShrink: 0, color: owner.endIconColor }}>{endIcon}</Box>
    </ListItemButton>
  );
}

const MOBILE_SOCIAL_ACTION_DEFAULT = Object.freeze({
  iconColor: "#1A1A2E",
  hoverIconColor: "#1A1A2E",
  backgroundColor: "#FFFFFF",
  borderColor: "rgba(26,26,46,.09)",
  hoverBackgroundColor: "#EEF0F5",
  focusColor: "#F35A3A",
});

function SocialButton({ href, label, children }) {
  if (!href) return null;
  return (
    <IconButton
      component="a"
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      sx={{
        width: 42,
        height: 42,
        border: "1px solid",
        borderColor: MOBILE_SOCIAL_ACTION_DEFAULT.borderColor,
        bgcolor: MOBILE_SOCIAL_ACTION_DEFAULT.backgroundColor,
        color: MOBILE_SOCIAL_ACTION_DEFAULT.iconColor,
        "&:hover": { bgcolor: MOBILE_SOCIAL_ACTION_DEFAULT.hoverBackgroundColor, color: MOBILE_SOCIAL_ACTION_DEFAULT.hoverIconColor },
        "&:focus-visible": { outline: `3px solid ${MOBILE_SOCIAL_ACTION_DEFAULT.focusColor}`, outlineOffset: 2 },
      }}
    >
      {children}
    </IconButton>
  );
}

function MobileCatalogPanel({
  allProductsLabel,
  productCount,
  loading,
  loadingCategoriesText,
  error,
  emptyCategoriesText,
  categories,
  latestProducts,
  displayMode,
  onProducts,
  onCategory,
  onSubcategory,
  onItem,
  panelStyle,
  itemStyle,
}) {
  const showCategories = displayMode !== "items";
  const showItems = displayMode !== "categories";
  return (
    <Box
      sx={{
        mt: 0.7,
        mb: 0.9,
        ml: 0.8,
        p: 1.1,
        borderRadius: 2.4,
        bgcolor: panelStyle.backgroundColor,
        color: itemStyle.textColor,
        border: "1px solid",
        borderColor: panelStyle.borderColor,
        boxShadow: panelStyle.boxShadow,
      }}
    >
      {allProductsLabel ? (
        <Button
          data-landing-field="navigation.allProductsLabel"
          fullWidth
          variant="contained"
          onClick={onProducts}
          endIcon={<ChevronRightRoundedIcon />}
          sx={{
            justifyContent: "space-between",
            minHeight: 44,
            borderRadius: 1.8,
            mb: 1,
            color: "#FFFFFF",
            bgcolor: "#F35A3A",
            "&:hover": { bgcolor: "#E44E31" },
            "&:focus-visible": { outline: "3px solid #F35A3A", outlineOffset: 2 },
          }}
        >
          {allProductsLabel}
          <Chip
            size="small"
            label={productCount}
            sx={{ height: 22, bgcolor: "rgba(255,255,255,.18)", color: "inherit", fontWeight: 900 }}
          />
        </Button>
      ) : null}

      {loading ? (
        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ py: 2, px: 0.5 }}>
          <CircularProgress size={18} sx={{ color: itemStyle.focusColor }} />
          {loadingCategoriesText ? <Typography variant="body2" sx={{ color: itemStyle.mutedTextColor }}>{loadingCategoriesText}</Typography> : null}
        </Stack>
      ) : error ? (
        <Alert severity="warning">{error}</Alert>
      ) : (
        <>
          {showCategories ? (
            categories.length === 0 ? (
              emptyCategoriesText ? <Typography variant="body2" sx={{ p: 1, color: itemStyle.mutedTextColor }}>{emptyCategoriesText}</Typography> : null
            ) : (
              <Box sx={{ display: "grid", maxHeight: "34dvh", overflowY: "auto", overscrollBehavior: "contain" }}>
                {categories.map((category) => (
                  <Box key={category.value} sx={{ py: 0.2 }}>
                    <Button
                      fullWidth
                      onClick={() => onCategory(category.query || category.value)}
                      sx={{
                        minHeight: 42,
                        px: 1,
                        justifyContent: "flex-start",
                        color: itemStyle.textColor,
                        borderRadius: 1.5,
                        textAlign: "left",
                        "&:hover": { bgcolor: itemStyle.hoverBackgroundColor },
                        "&:focus-visible": { outline: `3px solid ${itemStyle.focusColor}`, outlineOffset: 2 },
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {category.label}
                      </Typography>
                      <Typography variant="caption" sx={{ ml: 1, color: itemStyle.mutedTextColor }}>{category.count}</Typography>
                    </Button>

                    {category.subcategories?.length ? (
                      <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.55} sx={{ px: 1, pb: 0.8 }}>
                        {category.subcategories.map((subcategory) => (
                          <Chip
                            key={`${category.slug || category.value}-${subcategory.slug || subcategory.value}`}
                            size="small"
                            clickable
                            variant="outlined"
                            label={`${subcategory.label} · ${subcategory.count}`}
                            onClick={() => onSubcategory(subcategory.query || { category: category.value, subcategory: subcategory.value })}
                            sx={{
                              color: itemStyle.textColor,
                              borderColor: "rgba(26,26,46,.09)",
                              "&:hover": { bgcolor: itemStyle.hoverBackgroundColor },
                              "&:focus-visible": { outline: `3px solid ${itemStyle.focusColor}`, outlineOffset: 2 },
                            }}
                          />
                        ))}
                      </Stack>
                    ) : null}
                  </Box>
                ))}
              </Box>
            )
          ) : null}

          {showItems && latestProducts?.length ? (
            <Stack spacing={0.45} sx={{ mt: showCategories ? 0.8 : 0 }}>
              {latestProducts.map((item) => (
                <Button
                  key={item.id}
                  fullWidth
                  onClick={() => onItem?.(item.path)}
                  endIcon={<ChevronRightRoundedIcon />}
                  sx={{ justifyContent: "space-between", minHeight: 40, px: 1, color: itemStyle.textColor, "&:hover": { bgcolor: itemStyle.hoverBackgroundColor }, "&:focus-visible": { outline: `3px solid ${itemStyle.focusColor}`, outlineOffset: 2 } }}
                >
                  <Typography variant="body2" noWrap sx={{ fontWeight: 800, textAlign: "left", minWidth: 0 }}>
                    {item.name}
                  </Typography>
                </Button>
              ))}
            </Stack>
          ) : null}
        </>
      )}
    </Box>
  );
}

export default function NavbarMobileDrawer({
  open,
  location,
  business,
  logoUrl,
  compactLogoUrl,
  productsEnabled,
  gastronomicMenuEnabled,
  sections,
  navigation,
  mobileStyle,
  user,
  categories,
  latestProducts,
  productCount,
  displayMode,
  activeMegaButtonId,
  loading,
  error,
  categoriesOpen,
  whatsappUrl,
  onClose,
  onHome,
  onProducts,
  onGastronomicMenu,
  onAbout,
  onHighlights,
  onContact,
  onCustomButton,
  onItem,
  onLogin,
  membershipsEnabled = false,
  onAppointments,
  onMemberships,
  onLogout,
  onCategory,
  onSubcategory,
  onToggleCategories,
}) {
  const productsLabel = String(navigation?.productsLabel ?? "").trim();
  const defaultGastronomicNavigation = isCanonicalGastronomicCatalogNavigation(navigation, gastronomicMenuEnabled);
  const allProductsLabel = String(navigation?.allProductsLabel ?? "").trim();
  const loadingCategoriesText = String(navigation?.loadingCategoriesText ?? "").trim();
  const emptyCategoriesText = String(navigation?.emptyCategoriesText ?? "").trim();
  const loginLabel = String(navigation?.loginLabel ?? "").trim();
  const hasSocials = Boolean(whatsappUrl || business?.socials?.instagram || business?.socials?.facebook);
  const showAccessBlock = navigation?.showCustomerAccess !== false && Boolean(user || loginLabel);
  const customButtons = Array.isArray(navigation?.buttons)
    ? navigation.buttons
        .filter((item) => String(item?.label || "").trim() && (item?.behavior === "mega-menu" || String(item?.target || "").trim()))
        .slice(0, 8)
    : [];
  const drawerStyle = navbarDrawerStyle(navigation);
  const drawerHeaderStyle = navbarMobileOwner(navigation, "drawerHeaderStyle");
  const closeButtonStyle = navbarMobileOwner(navigation, "closeButtonStyle");
  const catalogPanelStyle = navbarMobileOwner(navigation, "catalogPanelStyle");
  const catalogItemStyle = navbarMobileOwner(navigation, "catalogItemStyle");
  const drawerLoginStyle = navbarActionStyle(navigation, "login", "drawer");
  const drawerAccountStyle = navbarActionStyle(navigation, "account", "drawer");

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: "min(92vw, 410px)" },
          maxWidth: "100vw",
          bgcolor: drawerStyle.backgroundColor,
          borderLeft: "1px solid",
          borderColor: drawerStyle.borderColor,
          boxShadow: drawerStyle.boxShadow,
        },
      }}
    >
      <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ p: { xs: 1.5, sm: 2.2 }, bgcolor: drawerHeaderStyle.backgroundColor, borderBottom: "1px solid", borderColor: drawerHeaderStyle.borderColor, boxShadow: drawerHeaderStyle.boxShadow }}
        >
          <NavbarBrand
            compact
            surfaceStyle={navbarLogoSurfaceStyle(navigation, "drawer")}
            businessName={business?.name}
            homeLabel={navigation?.homeLabel}
            logoUrl={logoUrl}
            compactLogoUrl={compactLogoUrl}
            maxLogoHeight={mobileStyle?.logoMaxHeight}
            logoColor={mobileStyle?.logoColor || ""}
            textColor={navbarBusinessNameColor(navigation, "drawer")}
            onClick={onHome}
          />
          <IconButton
            onClick={onClose}
            aria-label={navigation?.closeMenuAriaLabel ?? "Cerrar menú"}
            sx={{
              width: closeButtonStyle.size,
              height: closeButtonStyle.size,
              color: closeButtonStyle.color,
              bgcolor: closeButtonStyle.backgroundColor,
              border: "1px solid",
              borderColor: closeButtonStyle.borderColor,
              "&:hover": {
                color: closeButtonStyle.hoverColor,
                bgcolor: closeButtonStyle.hoverBackgroundColor,
                borderColor: closeButtonStyle.hoverBorderColor,
              },
              "&:focus-visible": {
                outline: `3px solid ${closeButtonStyle.focusColor}`,
                outlineOffset: 2,
              },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Box sx={{ p: { xs: 1.5, sm: 2.2 }, pt: { xs: 1.2, sm: 1.7 } }}>
          <List disablePadding sx={{ display: "grid", gap: `${mobileStyle?.itemGap ?? 5.2}px` }}>
            {navigation?.showHomeLink !== false && String(navigation?.homeLabel || "").trim() ? <DrawerNavItem
              style={navbarLinkStyle(navigation, "home", "mobile")}
              active={location.pathname === "/"}
              label={navigation?.homeLabel}
              fieldPath="navigation.homeLabel"
              onClick={onHome}
              endIcon={<ChevronRightRoundedIcon />}
            /> : null}

            {gastronomicMenuEnabled && navigation?.showProductsLink !== false && productsLabel ? (
              <DrawerNavItem
                style={navbarLinkStyle(navigation, "catalog", "mobile")}
                active={location.pathname.startsWith("/carta")}
                label="Carta"
                onClick={onGastronomicMenu}
                endIcon={<ChevronRightRoundedIcon />}
              />
            ) : null}

            {productsEnabled && !defaultGastronomicNavigation && navigation?.showProductsLink !== false && productsLabel ? (
              <>
                <DrawerNavItem
                  style={navbarLinkStyle(navigation, "catalog", "mobile")}
                  active={["/producto", "/productos", "/servicio", "/servicios", "/catalogo"].some(
                    (path) => location.pathname.startsWith(path),
                  )}
                  label={productsLabel}
                  fieldPath="navigation.productsLabel"
                  onClick={onToggleCategories}
                  endIcon={
                    categoriesOpen ? (
                      <KeyboardArrowUpRoundedIcon />
                    ) : (
                      <KeyboardArrowDownRoundedIcon />
                    )
                  }
                />

                <Collapse in={categoriesOpen && !activeMegaButtonId} timeout="auto" unmountOnExit>
                  <MobileCatalogPanel
                    panelStyle={catalogPanelStyle}
                    itemStyle={catalogItemStyle}
                    allProductsLabel={allProductsLabel}
                    productCount={productCount}
                    loading={loading}
                    loadingCategoriesText={loadingCategoriesText}
                    error={error}
                    emptyCategoriesText={emptyCategoriesText}
                    categories={categories}
                    latestProducts={latestProducts}
                    displayMode={displayMode}
                    onProducts={onProducts}
                    onCategory={onCategory}
                    onSubcategory={onSubcategory}
                    onItem={onItem}
                  />
                </Collapse>
              </>
            ) : null}

            {sections?.showAbout !== false && navigation?.showAboutLink !== false ? (
              <DrawerNavItem
                style={navbarLinkStyle(navigation, "about", "mobile")}
                active={false}
                label={navigation?.aboutLabel}
                fieldPath="navigation.aboutLabel"
                onClick={onAbout}
                endIcon={<ChevronRightRoundedIcon />}
              />
            ) : null}

            {sections?.showHighlights !== false &&
            navigation?.showHighlightsLink !== false ? (
              <DrawerNavItem
                style={navbarLinkStyle(navigation, "highlights", "mobile")}
                active={false}
                label={navigation?.highlightsLabel}
                fieldPath="navigation.highlightsLabel"
                onClick={onHighlights}
                endIcon={<ChevronRightRoundedIcon />}
              />
            ) : null}

            {sections?.showContact !== false && navigation?.showContactLink !== false ? (
              <DrawerNavItem
                style={navbarLinkStyle(navigation, "contact", "mobile")}
                active={location.pathname.startsWith("/contact")}
                label={navigation?.contactLabel}
                fieldPath="navigation.contactLabel"
                onClick={onContact}
                endIcon={<ChevronRightRoundedIcon />}
              />
            ) : null}

            {customButtons.map((item, index) => {
              const itemId = String(item?.id || "").trim();
              const isMegaMenu = item?.behavior === "mega-menu";
              const isOpen = isMegaMenu && activeMegaButtonId === itemId && categoriesOpen;
              return (
                <React.Fragment key={itemId || `navbar-button-runtime-${index}`}>
                  <DrawerNavItem
                    style={navbarCustomButtonStyle(item, "mobile")}
                    active={isOpen}
                    label={String(item.label).trim()}
                    fieldPath={`navigation.buttons.${index}.label`}
                    onClick={() => onCustomButton?.(item)}
                    endIcon={
                      isMegaMenu ? (isOpen ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />) : (
                        <ChevronRightRoundedIcon />
                      )
                    }
                  />
                  {isMegaMenu ? (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <MobileCatalogPanel
                        panelStyle={catalogPanelStyle}
                        itemStyle={catalogItemStyle}
                        allProductsLabel={allProductsLabel}
                        productCount={productCount}
                        loading={loading}
                        loadingCategoriesText={loadingCategoriesText}
                        error={error}
                        emptyCategoriesText={emptyCategoriesText}
                        categories={categories}
                        latestProducts={latestProducts}
                        displayMode={displayMode}
                        onProducts={onProducts}
                        onCategory={onCategory}
                        onSubcategory={onSubcategory}
                        onItem={onItem}
                      />
                    </Collapse>
                  ) : null}
                </React.Fragment>
              );
            })}
          </List>
        </Box>

        <Box sx={{ mt: "auto", p: { xs: 1.5, sm: 2.2 }, pt: 1, pb: "max(12px, env(safe-area-inset-bottom))" }}>
          {showAccessBlock ? (
            <Box
              sx={{
                p: user ? 1.7 : 0,
                borderRadius: 2.5,
                bgcolor: user ? drawerAccountStyle.containerBackgroundColor : "transparent",
                color: user ? drawerAccountStyle.primaryTextColor : "inherit",
                border: user ? "1px solid" : "none",
                borderColor: user ? drawerAccountStyle.containerBorderColor : "transparent",
              }}
            >
              {user ? (
                <>
                  <Stack direction="row" spacing={1.1} alignItems="center" sx={{ mb: 1.3 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "50%",
                        bgcolor: drawerAccountStyle.avatarBackgroundColor,
                        color: drawerAccountStyle.avatarTextColor,
                        fontWeight: 900,
                      }}
                    >
                      {String(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography data-landing-field="navigation.accountLabel" sx={{ fontWeight: 900 }} noWrap>
                        {user?.name || (navigation?.accountLabel ?? "Mi cuenta")}
                      </Typography>
                      <Typography variant="caption" noWrap sx={{ color: drawerAccountStyle.secondaryTextColor }}>
                        {user?.email || ""}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={onAppointments}
                    startIcon={<EventAvailableRoundedIcon />}
                    sx={{ minHeight: 44, borderRadius: 1.8, mb: 1 }}
                  >
                    Mis turnos
                  </Button>
                  {membershipsEnabled ? (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={onMemberships}
                      startIcon={<CardMembershipRoundedIcon />}
                      sx={{ minHeight: 44, borderRadius: 1.8, mb: 1 }}
                    >
                      Mis mensualidades
                    </Button>
                  ) : null}
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={onLogout}
                    startIcon={<LogoutRoundedIcon />}
                    sx={{
                      minHeight: 44,
                      borderRadius: 1.8,
                      color: drawerAccountStyle.logoutColor,
                      bgcolor: drawerAccountStyle.logoutBackgroundColor,
                      borderColor: drawerAccountStyle.logoutBorderColor,
                      "&:hover": { color: drawerAccountStyle.logoutHoverColor, bgcolor: drawerAccountStyle.logoutHoverBackgroundColor, borderColor: drawerAccountStyle.logoutHoverBorderColor },
                      "&:focus-visible": { outline: `3px solid ${drawerAccountStyle.logoutFocusColor}`, outlineOffset: 2 },
                    }}
                  >
                    {navigation?.logoutLabel ?? "Cerrar sesión"}
                  </Button>
                </>
              ) : loginLabel ? (
                <Button
                  data-landing-field="navigation.loginLabel"
                  fullWidth
                  variant="contained"
                  onClick={onLogin}
                  startIcon={<PersonOutlineRoundedIcon />}
                  sx={{
                    minHeight: 46,
                    borderRadius: 1.8,
                    color: drawerLoginStyle.color,
                    bgcolor: drawerLoginStyle.backgroundColor,
                    borderColor: drawerLoginStyle.borderColor,
                    "&:hover": { color: drawerLoginStyle.hoverColor, bgcolor: drawerLoginStyle.hoverBackgroundColor, borderColor: drawerLoginStyle.hoverBorderColor },
                  }}
                >
                  {loginLabel}
                </Button>
              ) : null}
            </Box>
          ) : null}

          {navigation?.showSocialLinks !== false && hasSocials ? (
            <>
              <Divider sx={{ my: 2, borderColor: "rgba(26,26,46,.09)" }} />
              <Stack direction="row" spacing={0.8} justifyContent="center">
                <SocialButton href={whatsappUrl} label="WhatsApp">
                  <WhatsAppIcon />
                </SocialButton>
                <SocialButton href={business?.socials?.instagram} label="Instagram">
                  <InstagramIcon />
                </SocialButton>
                <SocialButton href={business?.socials?.facebook} label="Facebook">
                  <FacebookIcon />
                </SocialButton>
              </Stack>
            </>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
}
